import { AlertTriangle, Ban, UserCog } from "lucide-react";
import type { RefusalType, RefusalV2 } from "@/lib/api";

interface RefusalBannerProps {
  refusal: RefusalV2;
}

/**
 * Bannière de refus normalisée (CdC #5 — 3 catégories Manus).
 *
 * - `prudent` (orange) : réponse mais avec réserve. Le syllogisme reste
 *   affiché à côté.
 * - `refus` (rouge) : pas de réponse substantielle, mais on garde le
 *   syllogisme pour transparence.
 * - `escalade` (violet AI-Act) : criticité 4 — validation humaine
 *   obligatoire avant tout usage. C'est la catégorie la plus sévère.
 */
const REFUSAL_STYLES: Record<
  RefusalType,
  { fg: string; bg: string; border: string; icon: typeof AlertTriangle; title: string }
> = {
  prudent: {
    fg: "var(--alert-orange)",
    bg: "color-mix(in oklch, var(--alert-orange) 12%, transparent)",
    border: "color-mix(in oklch, var(--alert-orange) 35%, transparent)",
    icon: AlertTriangle,
    title: "Réponse prudente",
  },
  refus: {
    fg: "rgb(254 202 202)",
    bg: "color-mix(in oklch, var(--block-red) 14%, transparent)",
    border: "color-mix(in oklch, var(--block-red) 40%, transparent)",
    icon: Ban,
    title: "Refus juridique",
  },
  escalade: {
    fg: "var(--ai-violet)",
    bg: "color-mix(in oklch, var(--ai-violet) 14%, transparent)",
    border: "color-mix(in oklch, var(--ai-violet) 40%, transparent)",
    icon: UserCog,
    title: "Escalade humaine obligatoire (AI Act Art. 14)",
  },
};

export function RefusalBanner({ refusal }: RefusalBannerProps) {
  const styles = REFUSAL_STYLES[refusal.type] ?? REFUSAL_STYLES.prudent;
  const Icon = styles.icon;
  return (
    <div
      role="alert"
      className="rounded-md border p-4 flex items-start gap-3"
      style={{
        background: styles.bg,
        borderColor: styles.border,
        color: styles.fg,
      }}
    >
      <Icon size={16} className="shrink-0 mt-0.5" />
      <div className="text-sm space-y-1">
        <p className="font-semibold">{styles.title}</p>
        <p style={{ lineHeight: 1.55 }}>{refusal.message}</p>
      </div>
    </div>
  );
}
